import { useState, useEffect } from 'react'
import { Button } from '../common'
import { assignmentService, groupService } from '../../services'
import type {
  Client,
  Program,
  AssignmentInsert,
  SubscriptionTier,
  Group,
} from '../../types/database.types'

type AssignmentMode = 'individual' | 'multiple' | 'group'

interface GroupWithMembers extends Group {
  member_count?: number
}

interface AssignmentFormProps {
  clients: Client[]
  programs: Program[]
  groups?: GroupWithMembers[]
  providerId: string
  providerTier: SubscriptionTier
  onSubmit: (data: AssignmentInsert) => Promise<void>
  onBulkSubmit?: (data: AssignmentInsert[]) => Promise<void>
  onCancel: () => void
  isLoading?: boolean
  preselectedClientId?: string
  preselectedProgramId?: string
}

export function AssignmentForm({
  clients,
  programs,
  groups = [],
  providerId,
  providerTier,
  onSubmit,
  onBulkSubmit,
  onCancel,
  isLoading = false,
  preselectedClientId,
  preselectedProgramId,
}: AssignmentFormProps) {
  const [assignmentMode, setAssignmentMode] = useState<AssignmentMode>(
    preselectedClientId ? 'individual' : 'individual'
  )
  const [clientId, setClientId] = useState(preselectedClientId || '')
  const [selectedClientIds, setSelectedClientIds] = useState<Set<string>>(new Set())
  const [selectedGroupId, setSelectedGroupId] = useState('')
  const [programId, setProgramId] = useState(preselectedProgramId || '')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState('')
  const [clientSearch, setClientSearch] = useState('')
  const [isLoadingGroupMembers, setIsLoadingGroupMembers] = useState(false)

  useEffect(() => {
    if (preselectedClientId) setClientId(preselectedClientId)
    if (preselectedProgramId) setProgramId(preselectedProgramId)
  }, [preselectedClientId, preselectedProgramId])

  // Load group members when a group is selected
  useEffect(() => {
    const loadGroupMembers = async () => {
      if (selectedGroupId && assignmentMode === 'group') {
        setIsLoadingGroupMembers(true)
        try {
          const memberIds = await groupService.getGroupMemberIds(selectedGroupId)
          setSelectedClientIds(new Set(memberIds))
        } catch (err) {
          console.error('Failed to load group members:', err)
        } finally {
          setIsLoadingGroupMembers(false)
        }
      }
    }
    loadGroupMembers()
  }, [selectedGroupId, assignmentMode])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!programId) {
      setError('Please select a program')
      return
    }

    // Calculate expiration based on program duration (with tier-based fallback)
    const selectedProgram = programs.find((p) => p.id === programId)
    const expiresAt = assignmentService.getAccessExpirationForProgramDuration(
      selectedProgram?.duration_weeks ?? null,
      providerTier
    )

    if (assignmentMode === 'individual') {
      if (!clientId) {
        setError('Please select a client')
        return
      }

      try {
        await onSubmit({
          client_id: clientId,
          program_id: programId,
          provider_id: providerId,
          notes: notes.trim() || null,
          expires_at: expiresAt.toISOString(),
          status: 'active',
        })
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create assignment')
      }
    } else {
      // Bulk or group assignment
      if (selectedClientIds.size === 0) {
        setError(
          assignmentMode === 'group'
            ? 'Selected group has no members'
            : 'Please select at least one client'
        )
        return
      }

      if (!onBulkSubmit) {
        setError('Bulk assignment is not supported')
        return
      }

      const assignments: AssignmentInsert[] = Array.from(selectedClientIds).map(
        (cId) => ({
          client_id: cId,
          program_id: programId,
          provider_id: providerId,
          notes: notes.trim() || null,
          expires_at: expiresAt.toISOString(),
          status: 'active',
        })
      )

      try {
        await onBulkSubmit(assignments)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create assignments')
      }
    }
  }

  const activeClients = clients.filter(
    (c) => c.status === 'active' || c.status === 'invited'
  )

  const filteredClients = clientSearch
    ? activeClients.filter(
        (c) =>
          c.full_name.toLowerCase().includes(clientSearch.toLowerCase()) ||
          c.email.toLowerCase().includes(clientSearch.toLowerCase())
      )
    : activeClients

  const toggleClientSelection = (cId: string) => {
    const newSelected = new Set(selectedClientIds)
    if (newSelected.has(cId)) {
      newSelected.delete(cId)
    } else {
      newSelected.add(cId)
    }
    setSelectedClientIds(newSelected)
  }

  const selectAllClients = () => {
    setSelectedClientIds(new Set(filteredClients.map((c) => c.id)))
  }

  const clearSelection = () => {
    setSelectedClientIds(new Set())
  }

  const hasPaidTier = providerTier && providerTier !== 'free'
  const canUseBulkAssign = hasPaidTier && groups.length > 0

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Program Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Program
        </label>
        <select
          value={programId}
          onChange={(e) => setProgramId(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          required
        >
          <option value="">Select a program</option>
          {programs.map((program) => (
            <option key={program.id} value={program.id}>
              {program.title}
            </option>
          ))}
        </select>
        {programs.length === 0 && (
          <p className="mt-1 text-sm text-yellow-600">
            No programs available. Create a program first.
          </p>
        )}
      </div>

      {/* Assignment Mode Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Assign To
        </label>
        <div className="space-y-2">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              name="assignmentMode"
              value="individual"
              checked={assignmentMode === 'individual'}
              onChange={() => {
                setAssignmentMode('individual')
                setSelectedClientIds(new Set())
                setSelectedGroupId('')
              }}
              className="h-4 w-4 text-blue-600"
            />
            <span className="text-gray-700">Individual Client</span>
          </label>
          {canUseBulkAssign && (
            <>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="assignmentMode"
                  value="multiple"
                  checked={assignmentMode === 'multiple'}
                  onChange={() => {
                    setAssignmentMode('multiple')
                    setClientId('')
                    setSelectedGroupId('')
                  }}
                  className="h-4 w-4 text-blue-600"
                />
                <span className="text-gray-700">Multiple Clients</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="assignmentMode"
                  value="group"
                  checked={assignmentMode === 'group'}
                  onChange={() => {
                    setAssignmentMode('group')
                    setClientId('')
                    setSelectedClientIds(new Set())
                  }}
                  className="h-4 w-4 text-blue-600"
                />
                <span className="text-gray-700">Entire Group</span>
              </label>
            </>
          )}
        </div>
      </div>

      {/* Individual Client Selection */}
      {assignmentMode === 'individual' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Client
          </label>
          <select
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          >
            <option value="">Select a client</option>
            {activeClients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.full_name} ({client.email})
              </option>
            ))}
          </select>
          {activeClients.length === 0 && (
            <p className="mt-1 text-sm text-yellow-600">
              No active clients. Add a client first.
            </p>
          )}
        </div>
      )}

      {/* Multiple Clients Selection */}
      {assignmentMode === 'multiple' && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Select Clients ({selectedClientIds.size} selected)
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={selectAllClients}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Select All
              </button>
              <button
                type="button"
                onClick={clearSelection}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear
              </button>
            </div>
          </div>
          <input
            type="text"
            placeholder="Search clients..."
            value={clientSearch}
            onChange={(e) => setClientSearch(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 mb-2"
          />
          <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
            {filteredClients.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                {clientSearch ? 'No clients match your search' : 'No active clients'}
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {filteredClients.map((client) => (
                  <li
                    key={client.id}
                    onClick={() => toggleClientSelection(client.id)}
                    className={`p-3 cursor-pointer hover:bg-gray-50 flex items-center gap-3 ${
                      selectedClientIds.has(client.id) ? 'bg-blue-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedClientIds.has(client.id)}
                      onChange={() => toggleClientSelection(client.id)}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {client.full_name}
                      </p>
                      <p className="text-sm text-gray-500 truncate">{client.email}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Group Selection */}
      {assignmentMode === 'group' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Select Group
          </label>
          <select
            value={selectedGroupId}
            onChange={(e) => setSelectedGroupId(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          >
            <option value="">Select a group</option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name} ({group.member_count || 0} members)
              </option>
            ))}
          </select>
          {groups.length === 0 && (
            <p className="mt-1 text-sm text-yellow-600">
              No groups available. Create a group first.
            </p>
          )}
          {isLoadingGroupMembers && (
            <p className="mt-1 text-sm text-blue-600">Loading group members...</p>
          )}
          {selectedGroupId && !isLoadingGroupMembers && selectedClientIds.size > 0 && (
            <p className="mt-1 text-sm text-green-600">
              {selectedClientIds.size} members will be assigned
            </p>
          )}
        </div>
      )}

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Notes for Client(s) (optional)
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Instructions or notes for your client(s)..."
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <p className="mt-1 text-sm text-gray-500">
          These notes will be visible to the client(s)
        </p>
      </div>

      {/* Access Duration Info */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900">Access Duration</h4>
        <p className="text-sm text-gray-600 mt-1">
          {programId && programs.find((p) => p.id === programId)?.duration_weeks ? (
            <>
              Clients will have access for{' '}
              <span className="font-medium">
                {programs.find((p) => p.id === programId)?.duration_weeks} weeks
              </span>{' '}
              (based on program duration)
            </>
          ) : (
            <>
              Access duration will be determined by your subscription tier (
              <span className="font-medium">
                {providerTier === 'basic'
                  ? '30 days'
                  : providerTier === 'platinum'
                  ? '1 year'
                  : '90 days'}
              </span>
              ) since this program has no set duration
            </>
          )}
        </p>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          isLoading={isLoading}
          disabled={
            (activeClients.length === 0 && assignmentMode !== 'group') ||
            programs.length === 0 ||
            (assignmentMode === 'group' && groups.length === 0)
          }
        >
          {assignmentMode === 'individual'
            ? 'Assign Program'
            : `Assign to ${selectedClientIds.size} Client${selectedClientIds.size !== 1 ? 's' : ''}`}
        </Button>
      </div>
    </form>
  )
}
